import { t } from 'i18next';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import { ISankeyNodeConfig, ISankeyNodeData } from '~/src/dash/components/charts/Sankey/common';
import { Col, Cols, Row, Rows, spacing, Span, Text } from '~/src/dash/publicaUI';
import { auctionInsightsKeyToTKeyMap } from '~/src/dash/utils/bidErrorCodes';
import { publicaColors } from '~/src/dash/utils/colors';
import { TKey } from '~/src/dash/publicaUI';

export const formatNodeTooltip = (
  nodeName: string,
  value: unknown,
  nodeConfigMap: Record<string, ISankeyNodeConfig>,
  nodeData?: ISankeyNodeData,
): string => {
  const nodeConfig = nodeConfigMap[nodeName];
  const formattedValue = typeof value === 'number' ? value.toLocaleString() : t('NotApplicable');
  const nodeColor = nodeConfig?.color || publicaColors.veryDarkGray;

  const individualErrorValues: { tKey: TKey; value: number }[] = [];
  if (nodeData?.values) {
    Object.entries(nodeData.values).forEach(([key, value]) => {
      if (typeof value === 'number' && value > 0) {
        individualErrorValues.push({
          tKey: auctionInsightsKeyToTKeyMap[key],
          value,
        });
      }
    });
    individualErrorValues.sort((a, b) => b.value - a.value);
  }

  return ReactDOMServer.renderToString(
    <Rows smallPadding>
      <Row noPadding>
        <Cols
          gap={spacing.extraSmall}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            width: 'max-content',
            minWidth: '100%',
            borderBottom: `1px solid ${nodeColor}`,
          }}
        >
          <Col style={{ flex: '0 0 auto', paddingRight: '16px' }}>
            <Text
              small
              bold
              color={publicaColors.veryDarkGray}
              style={{ whiteSpace: 'nowrap' }}
              tKey={nodeConfig?.tKey}
            />
          </Col>
          <Col style={{ flex: '0 0 auto', textAlign: 'right' }}>
            <Text small bold color={publicaColors.veryDarkGray} style={{ whiteSpace: 'nowrap' }}>
              {formattedValue}
            </Text>
          </Col>
        </Cols>
      </Row>
      {individualErrorValues.length > 0 &&
        individualErrorValues.map((error) => (
          <Row noPadding key={error.tKey}>
            <Cols
              gap={spacing.extraSmall}
              style={{
                justifyContent: 'space-between',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                width: 'max-content',
                minWidth: '100%',
              }}
            >
              <Col style={{ flex: '0 0 auto', paddingRight: '16px' }}>
                <Text small style={{ whiteSpace: 'nowrap' }} tKey={error?.tKey} />
              </Col>
              <Col style={{ flex: '0 0 auto', textAlign: 'right' }}>
                <Text small style={{ whiteSpace: 'nowrap' }}>
                  {error.value.toLocaleString()}
                </Text>
              </Col>
            </Cols>
          </Row>
        ))}
    </Rows>,
  );
};

export const formatLinkTooltip = (
  source: string,
  target: string,
  nodeConfigMap: Record<string, ISankeyNodeConfig>,
  value: number,
): string => {
  const sourceTKey = nodeConfigMap[source]?.tKey;
  const targetTKey = nodeConfigMap[target]?.tKey;
  const formattedValue = value.toLocaleString();

  return ReactDOMServer.renderToString(
    <Rows smallPadding>
      <Row noPadding>
        <Cols
          gap={spacing.extraSmall}
          style={{
            justifyContent: 'space-between',
            alignItems: 'center',
            whiteSpace: 'nowrap',
            width: 'max-content',
            minWidth: '100%',
          }}
        >
          <Col style={{ flex: '0 0 auto', paddingRight: '16px' }}>
            <Text small bold style={{ whiteSpace: 'nowrap' }}>
              <Span bold tKey={sourceTKey} /> → <Span bold tKey={targetTKey} />
            </Text>
          </Col>
          <Col style={{ flex: '0 0 auto', textAlign: 'right' }}>
            <Text small style={{ whiteSpace: 'nowrap' }}>
              {formattedValue}
            </Text>
          </Col>
        </Cols>
      </Row>
    </Rows>,
  );
};
